import { spring, interpolate } from 'remotion'

/** Spring configs */
export const SPRINGS = {
  gentle: { damping: 14, stiffness: 100, mass: 1 },
  snappy: { damping: 18, stiffness: 200, mass: 0.8 },
  bouncy: { damping: 10, stiffness: 150, mass: 1.2 },
  slow: { damping: 20, stiffness: 60, mass: 1 },
  elastic: { damping: 8, stiffness: 120, mass: 1.4 },
  smooth: { damping: 22, stiffness: 80, mass: 1 },
}

/* ── Entrance animations (0 → 1) ─────────────────────────────── */

/**
 * Returns a spring value 0→1 starting at `from` frame.
 */
export function entrance(frame, fps, from = 0, config = SPRINGS.snappy) {
  return spring({ frame: frame - from, fps, config })
}

/**
 * Fade opacity 0→1 over `duration` frames starting at `delay`.
 */
export function fadeIn(frame, delay = 0, duration = 20) {
  return interpolate(frame, [delay, delay + duration], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
}

/**
 * Slide Y by `offset` px, settling to 0.
 */
export function slideUp(frame, fps, from = 0, offset = 40, config = SPRINGS.snappy) {
  const progress = entrance(frame, fps, from, config)
  return interpolate(progress, [0, 1], [offset, 0])
}

/**
 * Scale from `startScale` to 1.
 */
export function scaleIn(frame, fps, from = 0, startScale = 0.85, config = SPRINGS.snappy) {
  const progress = entrance(frame, fps, from, config)
  return interpolate(progress, [0, 1], [startScale, 1])
}

/**
 * Staggered entrance delay for list items.
 * Returns the frame offset for item at index `i` with `stagger` frames between items.
 */
export function staggerDelay(i, stagger = 6) {
  return i * stagger
}

/* ── Exit animations (1 → 0) ─────────────────────────────────── */

/**
 * Fade opacity 1→0 starting at `startFrame`.
 */
export function fadeOut(frame, startFrame, duration = 20) {
  return interpolate(frame, [startFrame, startFrame + duration], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
}

/**
 * Combined fade: in at start, out near end of scene.
 * Returns opacity 0→1→1→0.
 */
export function fadeInOut(frame, inDelay, inDuration, outStart, outDuration) {
  const inVal = fadeIn(frame, inDelay, inDuration)
  const outVal = fadeOut(frame, outStart, outDuration)
  return Math.min(inVal, outVal)
}

/* ── Micro-animations (continuous / looping) ──────────────────── */

/**
 * Subtle breathing pulse. Returns scale multiplier ~0.98–1.02.
 * Use on elements that should feel "alive".
 */
export function breathe(frame, speed = 0.04, amplitude = 0.015) {
  return 1 + Math.sin(frame * speed) * amplitude
}

/**
 * Gentle vertical float. Returns Y offset in px.
 * Great for icons, particles, floating labels.
 */
export function float(frame, speed = 0.025, amplitude = 6) {
  return Math.sin(frame * speed) * amplitude
}

/**
 * Horizontal sway. Returns X offset in px.
 */
export function sway(frame, speed = 0.02, amplitude = 4) {
  return Math.sin(frame * speed + Math.PI / 3) * amplitude
}

/**
 * Pulsing glow intensity. Returns opacity multiplier 0.5–1.0.
 * Use for glow box-shadow animations.
 */
export function pulse(frame, speed = 0.06, min = 0.5, max = 1.0) {
  const t = (Math.sin(frame * speed) + 1) / 2
  return min + t * (max - min)
}

/* ── Camera / scene-level animations ──────────────────────────── */

/**
 * Subtle zoom over the full scene duration.
 * Returns scale value (e.g. 1.0 → 1.03).
 */
export function cameraZoom(frame, durationInFrames, from = 1.0, to = 1.03) {
  return interpolate(frame, [0, durationInFrames], [from, to], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
}

/* ── Scene lifecycle system ───────────────────────────────────────
   Divides a scene into three phases: ENTER → HOLD → EXIT.
   Returns normalized progress for each phase + convenience values.

   Usage:
     const lc = sceneLifecycle(frame, durationInFrames, { enterDur: 0.2, exitDur: 0.2 })
     lc.phase     // 'enter' | 'hold' | 'exit'
     lc.opacity   // 0→1 during enter, 1 during hold, 1→0 during exit
     lc.enter     // 0→1 during enter phase, 1 after
     lc.exit      // 1 before exit phase, 1→0 during exit
     lc.holdT     // 0→1 progress through hold phase (for micro-animations pacing)
   ────────────────────────────────────────────────────────────────── */

/**
 * @param {number} frame - Current frame
 * @param {number} dur - Total duration in frames
 * @param {object} opts
 * @param {number} opts.enterDur - Enter phase as fraction of total (0-1), default 0.18
 * @param {number} opts.exitDur - Exit phase as fraction of total (0-1), default 0.15
 */
export function sceneLifecycle(frame, dur, { enterDur = 0.18, exitDur = 0.15 } = {}) {
  const enterFrames = Math.round(dur * enterDur)
  const exitStart = dur - Math.round(dur * exitDur)

  const enter = interpolate(frame, [0, enterFrames], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const exit = interpolate(frame, [exitStart, dur], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const opacity = Math.min(enter, exit)

  const holdT =
    frame <= enterFrames
      ? 0
      : frame >= exitStart
        ? 1
        : (frame - enterFrames) / (exitStart - enterFrames)

  const phase = frame <= enterFrames ? 'enter' : frame >= exitStart ? 'exit' : 'hold'

  return { phase, enter, exit, opacity, holdT, enterFrames, exitStart }
}

/* ── Element lifecycle (staggered enter + global exit) ────────────
   For individual elements within a scene. Combines a per-element
   staggered entrance with the scene-level exit.
   ────────────────────────────────────────────────────────────────── */

/**
 * @param {number} frame
 * @param {number} dur - Total scene duration in frames
 * @param {number} enterDelay - Frame at which this element starts entering
 * @param {number} enterDuration - Frames for this element's entrance
 * @param {number} exitDur - Scene exit fraction (default 0.15)
 */
export function elementLifecycle(frame, dur, enterDelay, enterDuration = 15, exitDur = 0.15) {
  const exitStart = dur - Math.round(dur * exitDur)

  const enterOpacity = fadeIn(frame, enterDelay, enterDuration)
  const exitOpacity = fadeOut(frame, exitStart, Math.round(dur * exitDur))

  return {
    opacity: Math.min(enterOpacity, exitOpacity),
    entered: frame >= enterDelay + enterDuration,
    exiting: frame >= exitStart,
  }
}

/* ── Advanced stagger patterns ────────────────────────────────────
   Non-linear stagger for more organic entrance timing.
   ────────────────────────────────────────────────────────────────── */

/**
 * Wave stagger — items enter in a wave pattern (fast start, slow middle, fast end).
 */
export function waveStagger(i, total, totalDelay = 30) {
  const t = i / (total - 1 || 1)
  const wave = Math.sin(t * Math.PI) // 0→1→0 curve
  return Math.round(t * totalDelay * 0.6 + wave * totalDelay * 0.4)
}

/**
 * Cascade stagger — accelerating delay (items enter faster over time).
 */
export function cascadeStagger(i, baseDelay = 8) {
  return Math.round(baseDelay * Math.sqrt(i))
}
